import { Stack, HStack, Box, Flex, defineStyle, Separator, Center, Span } from '@chakra-ui/react';
import {
  Avatar,
  GenderAge,
  EditLineIcon,
  EditIconGhost,
  tools,
  EyeIcon,
  CopyIcon,
} from '@halo/ui-chat-react';
import { StateEnum, type User } from '@halo/chat-sdk-react';
import type { PropsWithChildren } from 'react';
import QrCodeButton from '@/components/QrCodeButton';
// import { ExpandableText } from '@/components/ExpandableText';
import { useActionsStore } from '@/store/actions';
import PreviewImage from './PreviewImage';

interface HeaderProps {
  user?: User | null;
  isMe: boolean;
  countryLabel: string;
  setEditFormVisible: (visible: boolean) => void;
}

const Header = ({ user, isMe, countryLabel, setEditFormVisible }: HeaderProps) => {
  const { setMyContactSettingIsOpen } = useActionsStore();
  const { t } = useTranslation();

  const handleEdit = useCallback(() => {
    setMyContactSettingIsOpen(true, user, true);
  }, [setMyContactSettingIsOpen, user]);

  const isDeleteAccount = useMemo(() => {
    return user?.state === StateEnum.DELETE_ACCOUNT;
  }, [user?.state]);

  const name = useMemo(
    () =>
      user?.state === StateEnum.DELETE_ACCOUNT
        ? t('chat.deleteAccountText')
        : `${user?.name ?? ''}${user?.secondary_name && `(${user.secondary_name})`}`,
    [user?.name, user?.secondary_name, user?.state, t]
  );

  return (
    <>
      <Stack
        className="StackHeader"
        gap="0"
        bg="bg.subtle"
        w="full"
        pb="2.5"
        roundedTopLeft="2xl"
        mt="-2.5"
      >
        <Box bg={isDeleteAccount ? '#c5c5c5' : 'transparent'}>
          <HStack className="HStack" gap="2.5" bg="bg" pl="4" roundedTopLeft="2xl">
            <Flex alignItems="center" gap="2.5" marginTop="-14px">
              <PreviewImageAvatar src={user?.avatar}>
                <Avatar
                  css={defineStyle({
                    outlineWidth: '3px',
                    outlineColor: 'bg',
                    outlineStyle: 'solid',
                  })}
                  src={user?.avatar}
                  name={name}
                  w={86}
                  height={86}
                  activeShake={false}
                />
              </PreviewImageAvatar>

              {isDeleteAccount && (
                <Box truncate lineHeight="shorter" fontWeight="semibold" fontSize="xl">
                  {name}
                </Box>
              )}
            </Flex>
            {!isDeleteAccount && (
              <Box flex="1 1 auto" overflow="hidden">
                <Flex
                  flex="1"
                  fontSize="xl"
                  gap="1"
                  mb="0.5"
                  pr="4"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Flex
                    alignItems="center"
                    gap="1"
                    overflow="hidden"
                    css={{
                      '&:hover': {
                        '--show-copy-name': 'block',
                      },
                    }}
                    flex="1"
                  >
                    <Box
                      truncate
                      lineHeight="shorter"
                      fontWeight="semibold"
                      onClick={() => tools.copyToClipboard(name)}
                      cursor="pointer"
                      maxW="80%"
                    >
                      {name}
                    </Box>
                    <GenderAge gender={user?.gender} age={user?.birthday} />
                    <Center
                      display="var(--show-copy-name, none)"
                      cursor="pointer"
                      color="fg.muted"
                      onClick={() => tools.copyToClipboard(name)}
                    >
                      <CopyIcon />
                    </Center>
                  </Flex>
                  <HStack gap="2.5">
                    <QrCodeButton user={user!} source="user" />
                    {!isMe && (
                      <Box cursor="pointer" p="1.5" rounded="xl" bg="grey.50">
                        <Center>
                          <EditLineIcon onClick={handleEdit} />
                        </Center>
                      </Box>
                    )}
                  </HStack>
                </Flex>
                <Flex
                  alignItems="center"
                  fontSize="sm"
                  color="fg.muted"
                  fontWeight="medium"
                  css={{
                    '&:hover': {
                      '--show-copy-name': 'block',
                    },
                  }}
                >
                  <Box
                    minW="50px"
                    truncate
                    onClick={() => tools.copyToClipboard(`@${user?.username}`)}
                    cursor="pointer"
                  >
                    @{user?.username}
                  </Box>
                  {user?.location && (
                    <>
                      <Separator
                        h="1em"
                        orientation="vertical"
                        mx="1.5"
                        borderColor="border.muted"
                      />
                      <Box truncate>{countryLabel}</Box>
                    </>
                  )}
                  <Center
                    display="var(--show-copy-name, none)"
                    cursor="pointer"
                    color="fg.muted"
                    mx="1"
                    onClick={() => {
                      tools.copyToClipboard(`@${user?.username}`);
                    }}
                  >
                    <CopyIcon />
                  </Center>
                </Flex>
              </Box>
            )}
          </HStack>
        </Box>
        <Box
          py="2.5"
          px="5"
          bg="linear-gradient(to bottom, var(--colors-bg), var(--colors-bg-subtle))"
        >
          {!isDeleteAccount && (
            // <ExpandableText text={user?.bio || ''}>
            //   {isMe ? (
            //     <HStack
            //       color="fg.muted"
            //       cursor="pointer"
            //       fontSize="sm"
            //       fontWeight="medium"
            //       onClick={() => setEditFormVisible(true)}
            //     >
            //       Say something to introduce yourself...
            //       <EditIcon />
            //     </HStack>
            //   ) : (
            //     'This user has no bio.'
            //   )}
            // </ExpandableText
            <HStack
              color="fg.muted"
              cursor={isMe ? 'pointer' : 'auto'}
              fontSize="sm"
              fontWeight="medium"
              overflow="hidden"
              gap="1"
              onClick={() => {
                if (isMe) {
                  setEditFormVisible(true);
                }
              }}
              css={{
                '&:hover': {
                  '--show-edit-bio': 'block',
                },
              }}
            >
              {user?.bio ? (
                <Span lineClamp={1}>{user?.bio}</Span>
              ) : isMe ? (
                <Span>{t('profile.bioPlaceholder')}</Span>
              ) : (
                <Span>{t('profile.noBio')}</Span>
              )}
              {isMe && (
                <Box flex="1" display="var(--show-edit-bio,none)">
                  <EditIconGhost />
                </Box>
              )}
            </HStack>
          )}
        </Box>
      </Stack>
    </>
  );
};

export const PreviewImageAvatar = (props: PropsWithChildren<{ src: string | undefined }>) => {
  const { src, children } = props;
  const [open, setOpen] = useState(false);
  const list = useMemo(() => [{ key: 'avatar', src: src, alt: 'avatar' }], [src]);
  return (
    <Box
      pos="relative"
      cursor="pointer"
      css={{
        '&:hover': {
          '--show-avatar': 'flex',
        },
      }}
      onClick={() => {
        setOpen(true);
      }}
    >
      {children}
      <Flex
        pos="absolute"
        top={0}
        left={0}
        alignItems="center"
        justifyContent="center"
        zIndex={10}
        w={86}
        height={86}
        bg="blackAlpha.70"
        rounded="50%"
        display="var(--show-avatar, none)"
      >
        <EyeIcon width="24px" height="24px" />
      </Flex>
      <PreviewImage
        slides={list}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </Box>
  );
};

const MemoizedHeader = memo(Header);
export { MemoizedHeader as Header };
